import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtNum, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { PlusIcon, PencilSimpleIcon, TrashIcon, WarehouseIcon, MapPinIcon, CoinsIcon, PrinterIcon, FileXlsIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const empty = { codigo: "", nombre: "", ubicacion: "", direccion: "", encargado: "", tipo: "secundaria", sucursal_id: "", activo: true };
const TIPOS = [
  { v: "principal", l: "Principal", color: "text-agro-green border-agro-green/40 bg-agro-green/10" },
  { v: "secundaria", l: "Secundaria", color: "text-text-primary border-border bg-app" },
  { v: "transito", l: "Tránsito", color: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10" },
  { v: "consignacion", l: "Consignación", color: "text-blue-400 border-blue-400/40 bg-blue-500/10" },
];

export default function Warehouses() {
  const [items, setItems] = useState([]);
  const [stocks, setStocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("bodegas");
  const [sucursales, setSucursales] = useState([]);
  const [verSuc, setVerSuc] = useState("todas");            // todas | <id> | sin
  const [verInactivas, setVerInactivas] = useState(false);
  const { user } = useAuth();
  const canValue = user?.role === "admin" || user?.role === "accountant";

  const load = async () => {
    setLoading(true);
    try {
      // Excluir bodega de tránsito del grid (es virtual, gestionada por el sistema)
      const { data } = await api.get("/warehouses", { params: { activo: verInactivas ? undefined : true, include_transito: false } });
      setItems(data);
      api.get("/branches").then(({ data: b }) => setSucursales(b || [])).catch(() => {});
      // Cargar resumen de stock por bodega
      const sm = {};
      await Promise.all(data.map(async (w) => {
        const r = await api.get(`/warehouses/${w.id}/stock`);
        const total = r.data.reduce((s, x) => s + (x.valor_total || 0), 0);
        const items = r.data.reduce((s, x) => s + (x.cantidad || 0), 0);
        sm[w.id] = { valor: total, items, productos: r.data.length };
      }));
      setStocks(sm);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [verInactivas]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/warehouses/${editing.id}`, { ...editing, ...form });
        toast.success("Bodega actualizada");
      } else {
        await api.post("/warehouses", form);
        toast.success("Bodega creada");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (er) { toast.error(apiErr(er)); }
  };
  const m = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave: save });

  const onEdit = (w) => { setEditing(w); setForm({ ...empty, ...w, sucursal_id: w.sucursal_id || "" }); setOpen(true); };
  const onActivar = async (w) => {
    try { await api.put(`/warehouses/${w.id}`, { ...w, activo: true }); toast.success("Bodega activada"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  // Grupos por sucursal (y las que aún no tienen sucursal asignada)
  const grupos = [
    ...sucursales.map((b) => ({ id: b.id, titulo: `${b.codigo} · ${b.nombre}`, items: items.filter((w) => w.sucursal_id === b.id) })),
    { id: "sin", titulo: "Sin sucursal asignada", items: items.filter((w) => !w.sucursal_id || !sucursales.some((b) => b.id === w.sucursal_id)) },
  ].filter((g) => (verSuc === "todas" ? (g.id !== "sin" || g.items.length) : g.id === verSuc));
  const onDel = async (w) => {
    if (!window.confirm(`¿Desactivar bodega ${w.codigo}?`)) return;
    try { await api.delete(`/warehouses/${w.id}`); toast.success("Bodega desactivada"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const totalGeneral = Object.values(stocks).reduce((s, x) => s + (x.valor || 0), 0);

  return (
    <Layout
      title="Bodegas por sucursal"
      subtitle={`${items.length} bodegas · ${sucursales.length} sucursales · Valor total ${fmtL(totalGeneral)}`}
      actions={
        <button data-testid="new-warehouse-btn"
          onClick={() => { setEditing(null); setForm({ ...empty, sucursal_id: ["todas", "sin"].includes(verSuc) ? "" : verSuc }); setOpen(true); }}
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-95">
          <PlusIcon size={16} weight="bold" /> Nueva bodega
        </button>
      }
    >
      {canValue && (
        <div className="border-b border-border mb-4 flex gap-1 print:hidden" data-testid="warehouses-tabs">
          <button onClick={() => setTab("bodegas")} data-testid="tab-bodegas"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 ${tab === "bodegas" ? "border-agro-green text-agro-green font-medium" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
            <WarehouseIcon size={15} /> Bodegas
          </button>
          <button onClick={() => setTab("valorizacion")} data-testid="tab-valorizacion"
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 ${tab === "valorizacion" ? "border-agro-green text-agro-green font-medium" : "border-transparent text-text-secondary hover:text-text-primary"}`}>
            <CoinsIcon size={15} /> Valorización
          </button>
        </div>
      )}
      {tab === "valorizacion" && canValue && <ValuationTab warehouses={items} />}
      {tab === "bodegas" && (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="warehouses-sucursal-filter">
          <span className="text-[10px] uppercase tracking-wider text-text-muted mr-1">Sucursal</span>
          {[{ id: "todas", l: "Todas" }, ...sucursales.map((b) => ({ id: b.id, l: `${b.codigo} · ${b.nombre}` })), { id: "sin", l: "Sin sucursal" }].map((o) => (
            <button key={o.id} onClick={() => setVerSuc(o.id)} data-testid={`suc-filter-${o.id}`}
              className={`px-3 py-1.5 text-xs rounded-md border ${verSuc === o.id ? "border-agro-green text-agro-green bg-agro-green/10" : "border-border text-text-secondary hover:text-text-primary"}`}>{o.l}</button>
          ))}
          <label className="ml-auto flex items-center gap-1.5 text-xs text-text-secondary cursor-pointer">
            <input type="checkbox" checked={verInactivas} onChange={(e) => setVerInactivas(e.target.checked)} data-testid="warehouses-inactivas" /> Mostrar inactivas
          </label>
        </div>
        {loading ? <div className="p-8 text-center text-text-muted">Cargando…</div> : grupos.map((g) => (
        <section key={g.id} data-testid={`warehouse-group-${g.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <h3 className={`font-heading text-base font-semibold ${g.id === "sin" ? "text-agro-yellow" : ""}`}>{g.titulo}</h3>
            <span className="text-xs text-text-muted">{g.items.length} bodega(s)</span>
            {g.id === "sin" && g.items.length > 0 && <span className="text-[11px] text-agro-yellow">· edítalas para asignarles su sucursal</span>}
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="warehouses-grid">
        {g.items.length === 0 ? (
          <div className="col-span-3 p-6 text-center text-sm text-text-muted border border-dashed border-border/60 rounded-lg">
            <WarehouseIcon size={24} className="inline mb-1 opacity-40" />
            <div>Esta sucursal aún no tiene bodegas</div>
          </div>
        ) : g.items.map((w) => {
          const tipoCfg = TIPOS.find((t) => t.v === w.tipo) || TIPOS[1];
          const st = stocks[w.id] || { valor: 0, items: 0, productos: 0 };
          return (
            <div key={w.id} className={`bg-card border border-border/60 rounded-lg p-5 hover:border-agro-green/40 transition-all ${!w.activo ? "opacity-50" : ""}`} data-testid={`warehouse-card-${w.codigo}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-mono text-xs text-agro-green mb-1">{w.codigo}</div>
                  <div className="font-heading font-semibold text-text-primary">{w.nombre}</div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${tipoCfg.color}`}>{tipoCfg.l}</span>
              </div>
              {w.ubicacion && (
                <div className="text-xs text-text-secondary flex items-center gap-1.5 mb-1">
                  <MapPinIcon size={12} /> {w.ubicacion}
                </div>
              )}
              {w.encargado && <div className="text-[11px] text-text-muted mb-3">Encargado: {w.encargado}</div>}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border/40">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Valor</div>
                  <div className="text-sm font-semibold text-agro-green">{fmtL(st.valor)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Productos</div>
                  <div className="text-sm font-semibold">{st.productos}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Unidades</div>
                  <div className="text-sm font-semibold">{fmtNum(st.items)}</div>
                </div>
              </div>
              <div className="flex gap-2 pt-3 mt-3 border-t border-border/40">
                <button onClick={() => onEdit(w)} className="flex-1 text-xs py-1.5 border border-border rounded hover:border-agro-green/40 text-text-secondary hover:text-text-primary flex items-center justify-center gap-1.5"
                  data-testid={`edit-warehouse-${w.codigo}`}>
                  <PencilSimpleIcon size={12} /> Editar
                </button>
                {!w.activo && (
                  <button onClick={() => onActivar(w)} className="text-xs py-1.5 px-3 border border-agro-green/30 rounded text-agro-green hover:bg-agro-green/10"
                    data-testid={`activate-warehouse-${w.codigo}`}>Activar</button>
                )}
                {w.activo && w.tipo !== "principal" && w.tipo !== "transito" && (
                  <button aria-label="Eliminar" onClick={() => onDel(w)} className="text-xs py-1.5 px-3 border border-border rounded hover:border-red-400/40 text-text-muted hover:text-red-400"
                    data-testid={`delete-warehouse-${w.codigo}`}>
                    <TrashIcon size={12} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
        </section>
        ))}
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="warehouse-modal">
            <div className="p-5 border-b border-border flex justify-between">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar bodega" : "Nueva bodega"}</h3>
              <BotonCerrar onClick={m.cerrar} />
            </div>
            <form onSubmit={m.guardar} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Código *</label>
                  <input required value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })}
                    data-testid="form-warehouse-codigo"
                    placeholder="BOD-XXX" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green font-mono uppercase" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tipo *</label>
                  <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    data-testid="form-warehouse-tipo"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
              </div>
              {form.tipo !== "transito" && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Sucursal *</label>
                  <select required value={form.sucursal_id || ""} onChange={(e) => setForm({ ...form, sucursal_id: e.target.value })}
                    data-testid="form-warehouse-sucursal" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="">— Selecciona la sucursal —</option>
                    {sucursales.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Nombre *</label>
                <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="form-warehouse-nombre"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Ubicación (ciudad / departamento)</label>
                <input value={form.ubicacion} onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                  data-testid="form-warehouse-ubicacion"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Dirección</label>
                <input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Encargado</label>
                <input value={form.encargado} onChange={(e) => setForm({ ...form, encargado: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-border/60 mt-2">
                <button type="button" onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button type="submit" data-testid="form-warehouse-submit" disabled={m.saving}
                  className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover">
                  {editing ? "Guardar cambios" : "Crear bodega"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function ValuationTab({ warehouses }) {
  const [f, setF] = useState({ bodega_id: "", categoria: "", proveedor: "", agrupar: "producto" });
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [sups, setSups] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async (ff = f) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(ff).filter(([, v]) => v));
      const { data: d } = await api.get("/inventory/valuation", { params });
      setData(d);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    api.get("/products/categories").then(({ data: c }) => setCats(c || [])).catch(() => {});
    api.get("/suppliers").then(({ data: s }) => setSups(s || [])).catch(() => {});
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportXlsx = async () => {
    try {
      const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), formato: "xlsx" };
      const res = await api.get("/inventory/valuation", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "valorizacion_inventario.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const upd = (k, v) => { const nf = { ...f, [k]: v }; setF(nf); run(nf); };
  const cols = {
    producto: [["codigo", "Código"], ["nombre", "Producto"], ["categoria", "Categoría"], ["proveedor", "Proveedor"]],
    bodega: [["bodega_codigo", "Bodega"], ["bodega_nombre", "Nombre"]],
    categoria: [["categoria", "Categoría"]],
    proveedor: [["proveedor", "Proveedor"]],
  }[f.agrupar] || [];

  return (
    <div className="space-y-4" data-testid="valuation-tab">
      <div className="bg-card border border-border/60 rounded-lg p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end print:hidden">
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Bodega</label>
          <select value={f.bodega_id} onChange={(e) => upd("bodega_id", e.target.value)} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" data-testid="val-bodega">
            <option value="">Todas</option>{warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo}</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Categoría</label>
          <select value={f.categoria} onChange={(e) => upd("categoria", e.target.value)} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" data-testid="val-categoria">
            <option value="">Todas</option>{cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Proveedor</label>
          <select value={f.proveedor} onChange={(e) => upd("proveedor", e.target.value)} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" data-testid="val-proveedor">
            <option value="">Todos</option>{sups.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select></div>
        <div><label className="text-[10px] uppercase text-text-muted block mb-1">Agrupar por</label>
          <select value={f.agrupar} onChange={(e) => upd("agrupar", e.target.value)} className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-sm" data-testid="val-agrupar">
            <option value="producto">Producto</option><option value="bodega">Bodega</option><option value="categoria">Categoría</option><option value="proveedor">Proveedor</option>
          </select></div>
        <button onClick={exportXlsx} className="border border-border rounded-md px-3 py-2 text-sm text-text-secondary flex items-center gap-1.5 justify-center"><FileXlsIcon size={14} /> Excel</button>
        <button onClick={() => window.print()} className="border border-border rounded-md px-3 py-2 text-sm text-text-secondary flex items-center gap-1.5 justify-center"><PrinterIcon size={14} /> Imprimir</button>
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              {cols.map(([k, l], i) => <th key={k} className={`p-3 ${i === 0 ? "pl-5" : ""}`}>{l}</th>)}
              <th className="p-3 text-right">Lotes</th><th className="p-3 text-right">Cantidad</th>
              <th className="p-3 text-right">Costo prom.</th><th className="p-3 pr-5 text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {loading || !data ? (
              <tr><td colSpan={cols.length + 4} className="p-8 text-center text-text-muted">Cargando…</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={cols.length + 4} className="p-8 text-center text-text-muted">Sin existencias para los filtros</td></tr>
            ) : data.rows.map((r, i) => (
              <tr key={i} className="border-t border-border/40">
                {cols.map(([k], j) => <td key={k} className={`p-3 text-xs ${j === 0 ? "pl-5 font-mono text-agro-green" : ""}`}>{r[k] || "—"}</td>)}
                <td className="p-3 text-right text-xs">{r.lotes}</td>
                <td className="p-3 text-right">{fmtNum(r.cantidad)}</td>
                <td className="p-3 text-right text-xs">{fmtL(r.costo_promedio)}</td>
                <td className="p-3 pr-5 text-right font-medium">{fmtL(r.valor)}</td>
              </tr>
            ))}
          </tbody>
          {data && (
            <tfoot className="bg-sidebar font-semibold">
              <tr>
                <td className="p-3 pl-5" colSpan={cols.length}>TOTAL</td>
                <td className="p-3 text-right">{data.totales.lotes}</td>
                <td className="p-3 text-right">{fmtNum(data.totales.cantidad)}</td>
                <td className="p-3"></td>
                <td className="p-3 pr-5 text-right text-agro-green">{fmtL(data.totales.valor)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
